import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { VISIBILITY_OPTIONS } from '@/lib/format';
import type { LabCredit, LabProjectDetail, LabRelease, LabVisibility } from '@/lib/types';

type FormState = {
  title: string;
  version: string;
  description: string;
  contentNotes: string;
  visibility: LabVisibility;
  credits: LabCredit[];
};

function toFormState(release: LabRelease): FormState {
  return {
    title: release.title,
    version: release.version,
    description: release.description ?? '',
    contentNotes: release.contentNotes ?? '',
    visibility: release.visibility,
    credits: release.credits,
  };
}

export function ReleaseDetailsForm({
  release,
  hasUnit,
  editable,
  onChange,
}: {
  release: LabRelease;
  hasUnit: boolean;
  editable: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  const loadedRelease = useRef(release);
  const [baseline, setBaseline] = useState<FormState>(() => toFormState(release));
  const [form, setForm] = useState<FormState>(baseline);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  useEffect(() => {
    const next = toFormState(release);
    if (release.id !== loadedRelease.current.id) {
      loadedRelease.current = release;
      setBaseline(next);
      setForm(next);
      return;
    }
    if (release.updatedAt === loadedRelease.current.updatedAt) return;
    if (JSON.stringify(form) !== JSON.stringify(next)) return;
    loadedRelease.current = release;
    setBaseline(next);
    setForm(next);
  }, [release, form]);
  const credits = form.credits.filter((credit) => credit.name.trim() && credit.contribution.trim());

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const updateCredit = (index: number, change: Partial<LabCredit>) =>
    update('credits', form.credits.map((credit, i) => (i === index ? { ...credit, ...change } : credit)));

  const save = async () => {
    setSaving(true);
    try {
      const detail = await labsApi.updateRelease(release.id, {
        title: form.title.trim(),
        version: form.version.trim(),
        description: form.description.trim() || null,
        contentNotes: form.contentNotes.trim() || null,
        visibility: form.visibility,
        credits,
      });
      onChange(detail);
      toast.success('Saved');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <fieldset disabled={!editable || saving} className="space-y-5">
        <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_10rem]">
          <div className="space-y-2">
            <Label htmlFor="release-title">Title</Label>
            <Input id="release-title" value={form.title} maxLength={120} onChange={(event) => update('title', event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="release-version">Version</Label>
            <Input id="release-version" value={form.version} maxLength={50} onChange={(event) => update('version', event.target.value)} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="release-description">Description</Label>
          <Textarea
            id="release-description"
            rows={5}
            maxLength={5000}
            value={form.description}
            onChange={(event) => update('description', event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="release-content-notes">Content notes</Label>
          <Textarea
            id="release-content-notes"
            rows={2}
            maxLength={1000}
            placeholder="For example: flashing lights, mild violence"
            value={form.contentNotes}
            onChange={(event) => update('contentNotes', event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="release-visibility">Who can play</Label>
          <Select value={form.visibility} onValueChange={(value) => update('visibility', value as LabVisibility)}>
            <SelectTrigger id="release-visibility" className="sm:w-80">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VISIBILITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} disabled={option.value === 'unit' && !hasUnit}>
                  {option.label} ({option.description})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <div className="flex min-h-8 items-center justify-between">
            <Label>Credits</Label>
            {editable && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => update('credits', [...form.credits, { name: '', contribution: '' }])}
                disabled={form.credits.length >= 50}
              >
                <Plus />
                Add credit
              </Button>
            )}
          </div>
          {form.credits.length === 0 && <p className="text-sm text-muted-foreground">No credits yet.</p>}
          {form.credits.map((credit, index) => (
            <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.25rem] items-center gap-3">
              <Input
                aria-label={`Credit ${index + 1} name`}
                placeholder="Name"
                maxLength={120}
                value={credit.name}
                disabled={Boolean(credit.permanent)}
                onChange={(event) => updateCredit(index, { name: event.target.value })}
              />
              <Input
                aria-label={`Credit ${index + 1} contribution`}
                placeholder="Contribution, for example Art"
                maxLength={120}
                value={credit.contribution}
                disabled={Boolean(credit.permanent)}
                onChange={(event) => updateCredit(index, { contribution: event.target.value })}
              />
              {editable && !credit.permanent ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove credit ${index + 1}`}
                  onClick={() => update('credits', form.credits.filter((_, i) => i !== index))}
                >
                  <X />
                </Button>
              ) : <span />}
            </div>
          ))}
        </div>
      </fieldset>

      {editable && (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" disabled={!dirty || saving} onClick={() => setForm(baseline)}>
            Discard
          </Button>
          <Button type="submit" disabled={!dirty || saving || !form.title.trim() || !form.version.trim()}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
    </form>
  );
}
